import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelectedHousehold } from '../hooks/useSelectedHousehold';
import { useRooms, useCreateRoom, useUpdateRoom, useDeleteRoom } from '../hooks/useRooms';
import { useChoreDefinitions } from '../hooks/useChoreDefinitions';
import { Modal } from '../components/Modal';
import { EmptyState, NoHouseholdEmpty } from '../components/EmptyState';
import { HouseholdSwitcher } from '../components/HouseholdSwitcher';
import type { Room } from '@chore-project/shared';

export function RoomsPage() {
  const { householdId } = useSelectedHousehold();
  const { data: rooms = [], isLoading } = useRooms(householdId);
  const { data: defs = [] } = useChoreDefinitions(householdId);
  const createRoom = useCreateRoom(householdId);
  const updateRoom = useUpdateRoom(householdId);
  const deleteRoom = useDeleteRoom(householdId);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const toggleExpand = (roomId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(roomId)) next.delete(roomId);
      else next.add(roomId);
      return next;
    });

  const openCreate = () => {
    setEditingRoom(null);
    setName('');
    setDescription('');
    setShowModal(true);
  };

  // Open the create modal when arriving from the quick-add (+) sheet.
  const location = useLocation();
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      openCreate();
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const openEdit = (room: Room, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingRoom(room);
    setName(room.name);
    setDescription(room.description ?? '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRoom(null);
    setName('');
    setDescription('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (editingRoom) {
      await updateRoom.mutateAsync({ roomId: editingRoom.roomId, name, description: description || undefined });
    } else {
      await createRoom.mutateAsync({ name, description: description || undefined });
    }
    closeModal();
  };

  const isPending = createRoom.isPending || updateRoom.isPending;

  if (!householdId) {
    return (
      <div className="page">
        <NoHouseholdEmpty />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page">
        <p className="loading-note">Loading rooms…</p>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1>Rooms</h1>
        <div className="page-header-actions">
          <HouseholdSwitcher />
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            + Add room
          </button>
        </div>
      </div>

      <section>
        <p className="section-label">Rooms — {rooms.length}</p>
        {rooms.length === 0 && (
          <EmptyState
            icon="grid-outline"
            title="No rooms yet"
            action={<button className="btn btn-primary" onClick={openCreate}>Add a room</button>}
          >
            Kitchen, Bathroom, Garden… rooms group chores by where they happen.
          </EmptyState>
        )}
        {rooms.map((room) => {
          const roomChores = defs.filter((d) => d.roomId === room.roomId);
          const isExpanded = expanded.has(room.roomId);
          return (
            <div key={room.roomId} className="card room-card">
              {/* Header row */}
              <div className="room-head" onClick={() => toggleExpand(room.roomId)}>
                <span className="room-caret">
                  {isExpanded ? '▾' : '▸'}
                </span>
                <div className="room-body">
                  <strong>{room.name}</strong>
                  {room.description && <div className="room-desc">{room.description}</div>}
                  <div className="room-count">
                    {roomChores.length} chore{roomChores.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="room-actions">
                  <button
                    className="btn btn-sm btn-ghost"
                    aria-label={`Edit ${room.name}`}
                    onClick={(e) => openEdit(room, e)}
                  >
                    <ion-icon name="create-outline" />
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    aria-label={`Delete ${room.name}`}
                    onClick={(e) => { e.stopPropagation(); void deleteRoom.mutate(room.roomId); }}
                    disabled={deleteRoom.isPending}
                  >
                    <ion-icon name="trash-outline" />
                  </button>
                </div>
              </div>

              {/* Expanded chore list */}
              {isExpanded && (
                <div className="room-chores">
                  {roomChores.length === 0 ? (
                    <p className="room-desc" style={{ margin: 0 }}>
                      No chores assigned to this room.
                    </p>
                  ) : (
                    roomChores.map((chore) => (
                      <div key={chore.choreDefId} className="room-chore-row">
                        <div>
                          <span className="room-chore-name">{chore.name}</span>
                          {chore.description && (
                            <span className="room-chore-desc">— {chore.description}</span>
                          )}
                        </div>
                        <span className="badge badge-neutral">
                          {chore.recurrence}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {showModal && (
        <Modal title={editingRoom ? 'Edit room' : 'Add room'} onClose={closeModal}>
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div className="field">
              <label htmlFor="room-name">Name</label>
              <input
                id="room-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Kitchen"
                required
                autoFocus
              />
            </div>
            <div className="field">
              <label htmlFor="room-desc">Description (optional)</label>
              <input
                id="room-desc"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Any additional details…"
              />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={isPending} className="btn btn-primary btn-full">
                {isPending ? 'Saving…' : editingRoom ? 'Save changes' : 'Add room'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
